import _ from "lodash";
import React, {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
  useMemo,
  useCallback,
} from "react";
import { useTranslation } from "react-i18next";

import {
  MagicWandThreeStarsOutlined,
  FourLinesOutlined,
  TwoLinesOutlined,
  StarsTwoFilled,
  AiOutlined,
  CloseOutlined,
  MicrophoneOutlined,
} from "@toddle-edu/ds-icons";
import {
  AIPromptBoxV2,
  IconButton,
  Tooltip,
  Modal,
  Button,
  Dialog,
} from "@toddle-edu/ds-web";
import { Popover, ArrowContainer } from "@toddle-edu/react-tiny-popover";
import { colors } from "Constants/colorConstants";
import classes from "./AIInputBox.scss";
import AIFeedbackStrip from "AppComponents/AIFeedbackStrip";
import {
  getMenuOptions,
  aiPromptsActionMapping,
  aiGenerateActionsMapping,
  getOffsetWithinElement,
  handleHighlightedTextRemoval,
  highlightText,
  setCaretPositionIncludingHTML,
  getNextBlockIndex,
  insertNextBlockHandler,
  cleanupMessage,
  getCaretPositionIncludingHTML,
  LANGUAGE_ACTIONS,
  cleanSelectedText,
  replaceDivWithNewLine,
} from "./utils";
import { isRTLText } from "Utils/StringHelpers";
import { isIphone, isIpad } from "Utils/DeviceDetectHelpers";

import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import {
  generateAIResponse,
  cancelRequest,
  uploadAttachments,
  updatePost,
  createAIConversation,
  onSendQuery,
} from "Post/modules/PostModule";
import { compose } from "lib/apolloClient";
import { connect } from "react-redux";
import {
  getFormattedUserNameWithoutPronoun,
  isPortfolioAIEnabled,
  isPortfolioAIEnabledAndIsJournalRoute,
  isPortfolioParityEnabled,
  isPortfolioReviewSetupEnabledAndIsReviewRoute,
  getIsNewGoalTaggingEnabled,
  isUnitPlanOrAssessmentTagged,
  isAssessmentTagged,
} from "Post/utils";
import ConditionalWrapper from "Utils/ConditionalWrapper";

import { getSettingValue } from "modules/PermissionModule";
import { getSpeechToTextTranscript } from "modules/Services";
import AudioRecorderSelector from "UIComponents/AudioRecorderSelector";
import ConfigureAIVoiceV2 from "AppComponents/ConfigureAIVoice/components/ConfigureAIVoiceV2";

import { AIOutlined } from "SvgComponents";
import { updateSettings } from "modules/ConfigurationThunks";
import { AI_TRANSLATION_LANGUAGES } from "@toddle-edu/common-utils";
import { isTabletByUserAgent } from "Utils";

const DIV_TAG = "<div>";

const NO_DIV_BR_TAG_REGEX = /<\/?div[^>]*>|<br\s?\/?>/gi;

const {
  REGENERATE,
  SHORTEN,
  ELABORATE,
  SIMPLIFY_LANGUAGE,
  FIX_SPELLING_GRAMMAR,
  IMPROVE_LANGUAGE,
  CHANGE_THIS,
  CHANGE_THIS_TEXT,
  ASK_AI_TO,
  AUTOGENERATE_CAPTION,
  AUTOGENERATE_WITH_IMAGE,
  ASK_AI_WITHOUT_IMAGE,
  ASK_AI_NOTE_CAPTION,
  ASK_AI_ATTACHMENT_CAPTION,
  ASK_AI_ATTACHMENT_CAPTION_FILLED,
} = aiGenerateActionsMapping;

const {
  KEEP,
  DISCARD,
  REPLACE,
  ADD_BELOW,
  TRANSLATE,
  TRANSLATE_BACK,
  DEFAULT_LANGUAGE,
} = aiPromptsActionMapping;

const styles = {
  popoverContainer: {
    zIndex: 1000,
  },
  fontStyle: {
    fontWeight: "normal",
  },
  tooltipStyle: {
    maxWidth: "260px",
    whiteSpace: "pre-wrap",
    textAlign: "center",
  },
  aiFeedbackStripNoContent: {
    borderTopLeftRadius: "6px",
    borderTopRightRadius: "6px",
  },
  aiFeedbackStrip: {
    marginTop: "6px",
    padding: "0px 12px",
  },
  aiFeedbackStripIconContainer: {
    gap: "8px",
  },
  customInfoIconStyle: { fill: "#FFBB33" },
  // Hidden input styles for iOS focus fix
  hiddenInput: {
    position: "fixed",
    top: -9999,
    left: -9999,
    opacity: 0,
    pointerEvents: "none",
    width: 1,
    height: 1,
  },
  aiVoiceContainerStyle: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
};

const BLANK_ARRAY = [];
const BLANK_STRING = "";

// iOS detection using existing helpers
const isIOSDevice = () => isIphone() || isIpad();

const getMenuOptionsStyle = optionsCount => {
  // used to show 7th option half visible.
  if (optionsCount === 0) return {};
  return optionsCount > 9 // dividers are also counted as options
    ? { maxHeight: `246px` }
    : { maxHeight: `${optionsCount * 36}px` };
};

function AIInputBox(props, ref) {
  const {
    showPromptBox,
    setShowPromptBox,
    showPromptBoxOverOptions,
    setShowPromptBoxOverOptions,
    textAreaTitle,
    setTextAreaTitle,
    textInputRef,
    generateAIResponse,
    updateTextAreaInputLocally,
    isFocussedRef,
    getTextAreaTitle,
    contentEditableRef,
    showMagicWand = true,
    aiIconVariant = "plain",
    aiButtonStyle = {},
    aiInputRef,
    placeholder,
    curriculumProgramId,
    attachmentsUrlMap,
    courseId,
    unitPlanId,
    leId,
    studentIds,
    studentMaskedIds,
    studentIdToNameMap,
    updatePost,
    uploadAttachments,
    isImageUploading,
    attachments,
    setImageUpload = () => {},
    askAIPromptAction,
    aiIconSize = "small",
    uploadAttachmentPrePostCreation = true,
    isLeTaggingRequired = true,
    isNote = false,
    useCustomAIIcon = false,
    goToStep,
    customPromptBoxStyles,
    elementPYP,
    selectFromAllPYP,
    parentContainerRef,
    responseStyle,
    promptIconStyles,
    userType,
    unitPlanTitle,
    isDP,
    taggedFolders,
    subjectNames,
    userName,
    isPromptBoxTextPresent,
    magicWandIconSize = "x-small",
    createAIConversation,
    onSendQuery,
    goToPreviousStep,
    maxAttachmentCount,
    setToastMsg,
    isUploadingInProgress,
    aiConversationId,
    configureAIVoiceProps,
    isPromptSetSelectionEnabled = false,
  } = props;
  const isTablet = isTabletByUserAgent();

  const { t } = useTranslation(["common", "journal"]);
  const [promptBoxContent, setPromptBoxContent] = useState("");
  const [promptBoxKeywords, setPromptBoxKeywords] = useState("");
  const [displayOptions, setDisplayOptions] = useState("noSelectionWand");
  const [isPromptDiscardModalOpen, setIsPromptDiscardModalOpen] = useState(
    false
  );
  const [isToddleAIQuery, setIsToddleAIQuery] = useState(false);
  const [isTextSelected, setTextSelected] = useState(false);
  const [isOptionsPopoverOpen, setIsOptionsPopoverOpen] = useState(false);
  const [previousActionDetails, setPreviousActionDetails] = useState({});
  const [generatingResponse, setGeneratingResponse] = useState(false);
  const [isResponseGenerated, setIsResponseGenerated] = useState(false);
  const [usageMetricsId, setUsageMetricsId] = useState("");
  const [model, setModel] = useState(null);
  const [currentTemperature, setCurrentTemperature] = useState(null);
  const [userInputFlagged, setUserInputFlagged] = useState(false);

  const promptBoxRef = useRef(null);
  const selectedTextRef = useRef(null);
  const optionsPopoverRef = useRef(null);
  const iosFocusInputRef = useRef(null); // React ref for iOS hidden input

  const enableChatGPTPoweredTextFeatures =
    isPortfolioAIEnabledAndIsJournalRoute(userType) ||
    (isPortfolioAIEnabled() && isPortfolioReviewSetupEnabledAndIsReviewRoute());

  const highlightedTextRef = useRef("");
  const popoverPositionRef = useRef(null);
  const clickedOnWandRef = useRef(false);
  const cursorPositionIncludingHTMLRef = useRef(0);
  const cursorPositionRef = useRef(0);
  const isDiscarded = useRef(false);
  const showPromptBoxRef = useRef(showPromptBox);
  const [isToddleAIDisabled, setIsToddleAIDisabled] = useState(true);
  const [streamingText, setStreamingText] = useState(
    _.size(attachmentsUrlMap)
      ? _.size(attachmentsUrlMap) > 1
        ? t("journal:anonymising_image_plural")
        : t("journal:anonymising_image")
      : t("common:writing")
  );
  const prevTextRef = useRef(textAreaTitle);
  const generatingResponseRef = useRef(generatingResponse);
  const promptBoxContentRef = useRef(promptBoxContent);
  const cursorSelectionPositionRef = useRef(0);
  const [showLEtooltip, setShowLEtooltip] = useState(false);
  const [
    showGoalsRemovalWarningModal,
    setShowGoalsRemovalWarningModal,
  ] = useState(false);
  const popoverContainerRef = useRef();
  const promptBoxKeywordsRef = useRef(isPromptBoxTextPresent);
  const responseFontStyles = { ...styles.fontStyle, ...responseStyle };
  const isFireFox =
    typeof window !== "undefined" && window?.mozInnerScreenX != null;

  const micButtonRef = useRef(null);
  const [isAudioLoading, setIsAudioLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);

  const handleSttTranscription = async attachment => {
    const { getSpeechToTextTranscript: getSttTranscript } = props;
    setIsAudioLoading(true);
    setIsRecording(false);
    const transcriptionPayload = {
      entityId: aiConversationId,
      entityType: "AI_CONVERSATION",
    };
    transcriptionPayload.blobAttachment = attachment;
    const transcript = (await getSttTranscript(transcriptionPayload))
      ?.transcript;
    if (transcript) {
      const currentText = promptBoxRef.current?.innerText || "";
      const updatedText = _.trim(currentText)
        ? `${currentText} ${transcript}`
        : transcript;
      setPromptBoxKeywords(updatedText);
      if (promptBoxRef.current) {
        promptBoxRef.current.innerText = updatedText;
        promptBoxRef.current.dispatchEvent(
          new Event("input", { bubbles: true })
        );
      }
    }
    setIsAudioLoading(false);
    setIsRecording(false);
  };

  const handleSetChatbarVisible = visible => setIsRecording(!visible);

  const handleFileUpload = () => {
    setIsRecording(false);
  };

  useEffect(() => {
    setIsToddleAIDisabled(isImageUploading);
  }, [isImageUploading]);

  const focusInputElement = useCallback(() => {
    if (promptBoxRef?.current) {
      const inputElement = promptBoxRef.current.querySelector?.(
        'input, textarea, [contenteditable="true"]'
      );
      if (inputElement) {
        inputElement.focus({ preventScroll: true });
      } else {
        promptBoxRef.current.focus?.({ preventScroll: true });
      }
    }
  }, []);

  // iOS: Focus the hidden input to maintain user gesture chain
  const triggerIOSFocus = useCallback(() => {
    if (isIOSDevice() && iosFocusInputRef.current) {
      iosFocusInputRef.current.focus();
    }
  }, []);

  // Main focus handler
  const handlePromptBoxFocus = useCallback(
    (immediate = false) => {
      focusInputElement();
      if (!immediate) {
        requestAnimationFrame(() => {
          if (showPromptBoxRef.current) {
            focusInputElement();
          }
        });
      }
    },
    [focusInputElement]
  );

  const handleResponseStop = (shouldClosePromptBox = true) => {
    cancelRequest && cancelRequest();
    setGeneratingResponse(false);
    handlePromptBoxFocus();
    shouldClosePromptBox && resetPromptBox();
  };

  const updateAttachments = async () => {
    setImageUpload(true);
    try {
      // Separate image and non-image attachments
      const imageAttachments = _.filter(
        attachments,
        attachment => attachment?.type === "IMAGE"
      );
      const nonImageAttachments = _.filter(
        attachments,
        attachment => attachment?.type !== "IMAGE"
      );

      let updatedImageAttachments = imageAttachments;
      if (_.size(imageAttachments) > maxAttachmentCount) {
        updatedImageAttachments = _.slice(
          updatedImageAttachments,
          0,
          maxAttachmentCount
        );
      }

      const uploadedImageAttachments = await uploadAttachments({
        attachments: updatedImageAttachments,
        parentId: null,
        parentType: "POST",
      });

      if (_.size(imageAttachments) > maxAttachmentCount) {
        const maxImageAttachments = _.slice(
          uploadedImageAttachments,
          0,
          maxAttachmentCount
        );
        // Combine uploaded images with existing non-image attachments
        const allAttachments = _.concat(
          maxImageAttachments,
          nonImageAttachments
        );
        updatePost({ attachments: allAttachments });
        setImageUpload(!_.size(maxImageAttachments));
        setToastMsg({
          msg: t("journal:max_attachment_limit_reached"),
          type: "info",
          theme: "dark",
          customInfoIconStyle: styles.customInfoIconStyle,
          locale_params: [
            {
              isPlainText: true,
              theme: "dark",
              key: "size",
              value: maxAttachmentCount,
            },
          ],
        });
      } else {
        // Combine uploaded images with existing non-image attachments
        const allAttachments = _.concat(
          uploadedImageAttachments,
          nonImageAttachments
        );
        updatePost({ attachments: allAttachments });
        setImageUpload(!_.size(uploadedImageAttachments));
      }
    } catch (e) {
      !isPortfolioParityEnabled() && goToStep("CREATE_POST");
      isPortfolioParityEnabled() &&
        _.isFunction(goToPreviousStep) &&
        goToPreviousStep();
    }
  };
  useEffect(() => {
    if (showPromptBox) {
      handlePromptBoxFocus();
    }
    if (!isUploadingInProgress) {
      showMagicWand && uploadAttachmentPrePostCreation && updateAttachments();
    }
    return () => {
      updatePost({ isPromptBoxTextPresent: false });
    };
  }, [isUploadingInProgress]);

  useEffect(() => {
    if (textInputRef?.domElm) {
      textInputRef?.domElm.addEventListener("keyup", handleTextSelection);
      textInputRef?.domElm.addEventListener("mousedown", () =>
        resetSelection({})
      );
    }

    return () => {
      textInputRef?.domElm?.removeEventListener("keyup", handleTextSelection);
      textInputRef?.domElm?.removeEventListener("mousedown", () =>
        resetSelection({})
      );
    };
  }, [textInputRef]);

  useEffect(() => {
    if (isOptionsPopoverOpen || showLEtooltip) {
      parentContainerRef &&
        parentContainerRef?.current?.addEventListener("click", () => {
          setIsOptionsPopoverOpen(false);
          setShowLEtooltip(false);
        });
    } else {
      parentContainerRef &&
        parentContainerRef?.current?.removeEventListener("click", () => {
          setIsOptionsPopoverOpen(false);
          showLEtooltip(false);
        });
    }
  }, [isOptionsPopoverOpen, showLEtooltip]);

  useEffect(() => {
    const promptBoxCurrentRef = _.get(promptBoxRef, "current", "");
    if (showPromptBox) {
      handlePromptBoxFocus();
      promptBoxCurrentRef &&
        promptBoxCurrentRef?.addEventListener("keydown", handleKeyDown);
      promptBoxCurrentRef &&
        promptBoxCurrentRef?.addEventListener("click", () =>
          handleHighlightedSelection({})
        );
    }
    showPromptBoxRef.current = showPromptBox;
    if (!showPromptBox) {
      textAreaTitle &&
        setTextAreaTitle(
          handleHighlightedTextRemoval({ message: textAreaTitle })
        );
      promptBoxCurrentRef &&
        promptBoxCurrentRef?.removeEventListener("keydown", handleKeyDown);
      promptBoxCurrentRef &&
        promptBoxCurrentRef?.removeEventListener("click", () =>
          handleHighlightedSelection({})
        );
      updatePost({ isPromptBoxTextPresent: false });
    }
  }, [showPromptBox]);

  useEffect(() => {
    generatingResponseRef.current = generatingResponse;
    updatePost({ isAIResponseGenerating: generatingResponse });
  }, [generatingResponse]);

  useEffect(() => {
    promptBoxContentRef.current = promptBoxContent;
    updatePost({
      isPromptBoxContentPresent: _.size(promptBoxContentRef.current) > 0,
    });
  }, [promptBoxContent]);

  useEffect(() => {
    if (!_.isEqual(isPromptBoxTextPresent, promptBoxKeywordsRef.current)) {
      promptBoxKeywordsRef.current = isPromptBoxTextPresent;
    }
  }, [isPromptBoxTextPresent]);

  const handleCaptionBoxFocus = ({ updatedMessage }) => {
    const domElem = textInputRef.domElm;
    const message = !_.isUndefined(updatedMessage)
      ? updatedMessage
      : textAreaTitle;
    if (domElem) {
      setTextAreaTitle(handleHighlightedTextRemoval({ message }));
      highlightText({
        ref: contentEditableRef.current,
        text: handleHighlightedTextRemoval({
          message: highlightedTextRef.current,
        }),
      });
      setCaretPositionIncludingHTML(
        textInputRef.domElm,
        cursorPositionRef.current + 1
      );
    }
  };

  const handleKeyDown = event => {
    // handling backspace for promptBox
    if (
      showPromptBoxRef.current &&
      event.key === "Backspace" &&
      _.isEmpty(promptBoxKeywords) &&
      !generatingResponseRef.current &&
      !(
        promptBoxKeywordsRef.current &&
        !_.isEmpty(promptBoxRef.current?.textContent)
      )
    ) {
      event.preventDefault();
      resetPromptBox();
      setIsToddleAIQuery(false);
      resetAIMetrics();
      setPromptBoxContent("");
      handleCaptionBoxFocus({});
      textInputRef?.focus();
    }
    if (
      (showPromptBoxRef.current || isOptionsPopoverOpen) &&
      event.keyCode === 27
    ) {
      generatingResponseRef.current && handleResponseStop();
    }
  };

  const resetSelection = ({ isDiscarded = false }) => {
    if (!generatingResponseRef.current || isDiscarded) {
      setIsOptionsPopoverOpen(false);
      resetPromptBox();
      setTextSelected(false);
      setPromptBoxContent("");
      setPromptBoxKeywords("");
      setDisplayOptions("noSelectionWand");
      setIsToddleAIQuery(false);
      updateTextAreaInputLocally({
        title: handleHighlightedTextRemoval({
          message: getTextAreaTitle(),
        }),
      });
      resetAIMetrics();
      setIsPromptDiscardModalOpen(false);
      highlightedTextRef.current = "";
      clickedOnWandRef.current = false;
      popoverPositionRef.current = {};
      cursorPositionIncludingHTMLRef.current = 0;
      isPromptBoxTextPresent && updatePost({ isPromptBoxTextPresent: false });
    }
  };

  const setDisplayOptionsDefault = () => {
    setDisplayOptions("selectedTextDisabled");
  };

  const handleToddleAIGeneration = ({ message = "" }) => {
    let updatedMessage = message;

    // replacing '/' with incoming content
    if (_.endsWith(message, "/")) {
      updatedMessage = _.trimEnd(message, "/");
    } else if (_.endsWith(message, " /")) {
      updatedMessage = _.trimEnd(message, " /");
    }

    return updatedMessage;
  };

  const handleHighlightedSelection = ({ content = "" }) => {
    const updatedText = handleHighlightedTextRemoval({
      message: content || highlightedTextRef.current,
    });
    const message = getTextAreaTitle() || "";
    if (updatedText) {
      const spanStartTag =
        '<span style="background-color: rgba(106, 177, 255, 0.4);padding-top: 3px;">';
      const spanEndTag = "</span>";

      if (!_.includes(message, spanStartTag)) {
        // each new line is a <div> tag in base-ui box
        // "span" doesn't highlight beyond "div", so we need to have "span" for each "div"
        let parts = _.split(updatedText, DIV_TAG);
        parts = _.map(parts, part => {
          if (
            !_.startsWith(part, DIV_TAG) &&
            !_.startsWith(part, spanStartTag)
          ) {
            return `${spanStartTag}${part}${spanEndTag}`;
          }
          return part;
        });

        const transformedText = _.join(parts, DIV_TAG);

        // Replace the selected text with the span-wrapped in the original content
        const newValue = _.replace(message, updatedText, transformedText);

        // Update state
        setTextAreaTitle(newValue);
        highlightedTextRef.current = transformedText;
      }
    }
  };

  const updatePromptBoxStatus = params => {
    const message = cleanupMessage({
      message: _.toString(_.get(params, "message", "")),
    });
    if (_.isEmpty(message)) {
      setIsResponseGenerated(false);
    }
  };

  const updateTextAreaLocally = params => {
    const message = cleanupMessage({
      message: _.toString(_.get(params, "message", "")),
    });
    if (_.isEmpty(message)) {
      setIsResponseGenerated(false);
    }
    setTextAreaTitle(message);
    updateTextAreaInputLocally({ title: message });
  };

  const updatePromptBoxContentLocally = ({ content }) => {
    setPromptBoxContent(content);
  };

  const handleAIResponseGeneration = async ({
    context,
    action,
    textAreaTitle,
    isPromptAction = false,
    optionSelected,
    selectedText,
    userQuery = "",
    temperature = 0.85,
    isTextSelected,
    promptBoxContent,
  }) => {
    setPreviousActionDetails({
      context,
      action,
      textAreaTitle,
      isPromptAction,
      optionSelected,
      selectedText,
      userQuery,
      temperature: 1,
      isTextSelected,
      promptBoxContent,
    });
    const payload = {
      context,
      message: getTextAreaTitle(),
      action,
      updatePromptBox: isTextSelected,
      promptBoxContent,
      isPromptAction,
      optionSelected,
      temperature,
      selectedText,
      cursorPositionIncludingHTML: cursorPositionIncludingHTMLRef.current,
      getNextBlockIndex: getNextBlockIndex,
      insertNextBlockHandler: insertNextBlockHandler,
      handleToddleAIGeneration: handleToddleAIGeneration,
      updateTextAreaLocally: updateTextAreaInputLocally,
      updatePromptBoxContentLocally: updatePromptBoxContentLocally,
      cleanupMessage: cleanupMessage,
      curriculumProgramId: curriculumProgramId,
      courseId,
      unitPlanId,
      leId,
      studentIds,
      studentIdToNameMap,
      updatePromptStatus: setStreamingText,
      elementPYP,
      selectFromAllPYP,
      changeStreamText: !!_.size(attachmentsUrlMap),
      t,
    };
    const {
      isSuccess,
      content,
      updatedPostCaption,
      usageMetricsId,
      model,
      currentTemperature,
      userInputFlagged,
    } = await generateAIResponse(payload);
    setGeneratingResponse(false);
    setUsageMetricsId(usageMetricsId);
    setModel(model);
    setCurrentTemperature(currentTemperature);
    setUserInputFlagged(userInputFlagged);
    return {
      isSuccess,
      content,
      updatedPostCaption,
    };
  };

  const updatePromptFocusAndTextSelection = ({ content }) => {
    handleHighlightedSelection({ content });
    handlePromptBoxFocus();
  };

  const updatePromptResponse = ({ updatedMessage, content, callback }) => {
    updateTextAreaLocally({ message: updatedMessage });
    setIsResponseGenerated(true);
    setDisplayOptions(_.isEmpty(updatedMessage) ? "" : "noSelectionWand");
    setIsToddleAIQuery(false);
    setPromptBoxKeywords("");
    setTimeout(() => {
      callback && callback({ content });
    }, 0);
  };

  const handleChatGPTButtonsClick = async ({
    selectedText = "",
    optionSelected = "",
    isPromptAction = false,
    isRegenerateAction = false,
    updatedMessage = "",
    userQuery = "",
    isSelectionExist = false,
    isTriggeredUnfocussed = false,
    userPrompt = "",
    updatedPromptBoxContent,
    language,
  }) => {
    const isOutputDiscarded = isDiscarded.current;
    isDiscarded.current = false;
    prevTextRef.current = textAreaTitle;
    const isTextSelectionExist = isSelectionExist || isTextSelected;
    const message = isPromptAction ? updatedMessage : textAreaTitle;
    setGeneratingResponse(true);
    setShowPromptBox(true);
    setIsOptionsPopoverOpen(false);
    const action = isPromptAction ? optionSelected : userQuery;
    const isDPActions =
      isDP &&
      _.includes([ASK_AI_WITHOUT_IMAGE, ASK_AI_ATTACHMENT_CAPTION], action);
    let context = {};
    if (action === TRANSLATE) {
      context = {
        selectedText: !_.isEmpty(promptBoxContent)
          ? promptBoxContent
          : selectedText,
        language,
      };
    } else {
      context = {
        selectedText: !_.isEmpty(promptBoxContent)
          ? promptBoxContent
          : selectedText,
        userPrompt: promptBoxKeywords || userPrompt,
        ...(!_.isEmpty(studentMaskedIds) &&
          _.size(studentMaskedIds) <= 3 && {
            studentNames: studentMaskedIds,
          }),
        ...(!_.isEmpty(unitPlanTitle) &&
          !_.isEmpty(leId) && { unitName: unitPlanTitle }),
        ...(isDPActions &&
          !_.isEmpty(taggedFolders) && {
            tags: _.join(_.map(taggedFolders, "name"), ","),
          }),
        ...(isDPActions &&
          !_.isEmpty(subjectNames) && { subject: _.join(subjectNames, ",") }),
        // ...(((isNote && _.isEqual(action, ASK_AI_NOTE_CAPTION)) ||
        //   isDPActions) &&
        //   !_.isEmpty(userName) && { teacherName: "⌴teacher⌴" }), //removing the teacher name context as we don't need it for now
      };
    }
    if (
      (showMagicWand || isDP) &&
      _.size(attachmentsUrlMap) &&
      _.includes([AUTOGENERATE_WITH_IMAGE, ASK_AI_WITHOUT_IMAGE], userQuery)
    ) {
      context["imageUrls"] = attachmentsUrlMap;
    }
    const {
      isSuccess,
      content,
      updatedPostCaption,
    } = await handleAIResponseGeneration(
      isRegenerateAction
        ? { ...previousActionDetails, isTextSelected: isTextSelectionExist }
        : {
            action,
            context,
            message,
            isPromptAction,
            optionSelected,
            selectedText,
            userQuery: promptBoxKeywords,
            isTextSelected: isTextSelectionExist,
            promptBoxContent: updatedPromptBoxContent ?? promptBoxContent,
          }
    );

    if (isSuccess) {
      if (!isTextSelectionExist) {
        const updatedMessage = handleToddleAIGeneration({
          message: updatedPostCaption,
        });

        highlightedTextRef.current = content;
        updatePromptBoxStatus();
        updatePromptResponse({
          updatedMessage: isOutputDiscarded
            ? prevTextRef.current
            : updatedMessage,
          content,
          callback: updatePromptFocusAndTextSelection,
        });
        isDiscarded.current = false;
      } else {
        setDisplayOptions("promptBox");
        setPromptBoxKeywords("");
        handlePromptBoxFocus();
        handleHighlightedSelection({});
      }
    } else {
      setGeneratingResponse(false);
    }
  };

  const updatePromptContentStatus = ({ callback }) => {
    setPromptBoxContent("");
    setIsOptionsPopoverOpen(false);
    setTimeout(() => {
      callback && callback();
    }, 0);
  };

  const updatePromptContentOnRegenerate = ({ callback }) => {
    setPromptBoxContent("");
    callback && callback();
  };

  const resetAIMetrics = () => {
    setUsageMetricsId("");
    setModel(null);
    setCurrentTemperature(null);
    setUserInputFlagged(false);
  };

  const resetPromptBox = () => {
    setShowPromptBox(false);
    setShowPromptBoxOverOptions(false);
  };

  const createAIConversationHandler = async optionSelected => {
    if (
      !_.get(props, "aiConversationId") &&
      !_.includes([CHANGE_THIS, DISCARD, KEEP], optionSelected)
    ) {
      await createAIConversation();
    }
    !_.includes([CHANGE_THIS, DISCARD, KEEP], optionSelected) &&
      onSendQuery({
        content: optionSelected,
        metaData: {
          isUserInputMessage: false,
        },
        authorType: "user",
      });
  };
  const handlePromptOptionClick = ({
    key: optionSelected,
    label,
    isSelectionExist = false,
  }) => {
    const isTextSelectionExist = isSelectionExist || isTextSelected;
    const removedHighlightMessage = handleHighlightedTextRemoval({
      message: getTextAreaTitle(),
    });
    let cleanHighlightedText = handleHighlightedTextRemoval({
      message: highlightedTextRef.current,
    });

    const updatedMessage = _.replace(
      removedHighlightMessage,
      cleanHighlightedText,
      ""
    );
    if (_.some(LANGUAGE_ACTIONS, action => action.label === label)) {
      setPromptBoxContent("");
      if (_.startsWith(optionSelected, DEFAULT_LANGUAGE)) {
        const languageId = _.nth(_.split(optionSelected, ":"), 1) || "";
        if (!_.isEmpty(languageId)) {
          props.updateSettings({
            settings: [
              {
                name: "ConfigurePortfolioAIVoiceLanguage",
                value: languageId,
              },
            ],
            showToastMsg: false,
          });
        }
        resetPromptBox();
        return;
      }
      handleChatGPTButtonsClick({
        selectedText: promptBoxContent || cleanHighlightedText,
        language: label,
        isSelectionExist: true,
        updatedPromptBoxContent: "",
        isPromptAction: true,
        optionSelected: TRANSLATE,
      });
    }
    createAIConversationHandler(optionSelected);
    let message = "";
    let isTriggeredUnfocussed = false;
    const bulletPointRegex = /^- (.+)$/gm;
    switch (optionSelected) {
      case REGENERATE:
        updatePromptContentOnRegenerate({
          callback: () => {
            handleChatGPTButtonsClick({
              selectedText: promptBoxContent || cleanHighlightedText,
              optionSelected,
              isPromptAction: true,
              isRegenerateAction: true,
              isSelectionExist: true,
              userQuery: askAIPromptAction,
            });
          },
        });
        break;
      case CHANGE_THIS:
        if (isResponseGenerated) {
          setPromptBoxContent("");
          setTimeout(() => {
            handleChatGPTButtonsClick({
              selectedText: promptBoxContent || cleanHighlightedText,
              userQuery: askAIPromptAction,
              optionSelected: ASK_AI_ATTACHMENT_CAPTION_FILLED,
              isSelectionExist: true,
              updatedPromptBoxContent: "",
            });
          }, 0);
          return;
        }
        if (isTextSelectionExist) {
          setPromptBoxContent("");
          setTimeout(() => {
            handleChatGPTButtonsClick({
              selectedText: promptBoxContent || cleanHighlightedText,
              userQuery: askAIPromptAction,
              updatedPromptBoxContent: "",
            });
          }, 0);
        } else {
          let action = CHANGE_THIS_TEXT;
          if (_.isEmpty(textAreaTitle)) {
            action = ASK_AI_TO;
          }
          setTextAreaTitle(updatedMessage, () => {
            const updatedMessage = handleToddleAIGeneration({
              message: handleHighlightedTextRemoval({ message: textAreaTitle }),
            });
            const tagFreeMessage = _.replace(
              updatedMessage,
              NO_DIV_BR_TAG_REGEX,
              ""
            );
            handleChatGPTButtonsClick({
              selectedText: handleToddleAIGeneration({
                message: updatedMessage,
              }),
              userQuery: askAIPromptAction,
              optionSelected: action,
              updatedPromptBoxContent: "",
            });
          });
          highlightedTextRef.current = "";
        }
        break;
      case KEEP:
        resetPromptBox();
        resetAIMetrics();
        setTextSelected(false);
        setPromptBoxContent("");
        setIsToddleAIQuery(false);
        setTextAreaTitle(
          handleHighlightedTextRemoval({ message: textAreaTitle })
        );
        handleCaptionBoxFocus({
          updatedMessage: handleHighlightedTextRemoval({
            message: textAreaTitle,
          }),
        });
        highlightedTextRef.current = "";
        break;
      case DISCARD:
        if (isTextSelectionExist || promptBoxContent) {
          setPromptBoxContent("");
          resetPromptBox();
          resetAIMetrics();
          setTextSelected(false);
          setIsToddleAIQuery(false);
          handleCaptionBoxFocus({});
          highlightedTextRef.current = "";
        } else {
          updateTextAreaLocally({ message: "" });
          setTextAreaTitle("");
          resetPromptBox();
          resetAIMetrics();
          setTextSelected(false);
          handleCaptionBoxFocus({ updatedMessage: "" });
          highlightedTextRef.current = "";
        }
        break;
      case ELABORATE:
      case SHORTEN:
        cleanHighlightedText = cleanSelectedText({
          message: cleanHighlightedText,
        });
        if (isResponseGenerated) {
          setPromptBoxContent("");
          setTimeout(() => {
            handleChatGPTButtonsClick({
              selectedText: cleanHighlightedText,
              optionSelected,
              isPromptAction: true,
              updatedMessage,
              isSelectionExist: true,
              updatedPromptBoxContent: "",
            });
          }, 0);
          return;
        }
        if (isTextSelectionExist) {
          updatePromptContentStatus({
            callback: () => {
              handleChatGPTButtonsClick({
                selectedText: promptBoxContent || cleanHighlightedText,
                optionSelected,
                isPromptAction: true,
                updatedMessage,
                isSelectionExist: isTextSelectionExist,
                updatedPromptBoxContent: "",
              });
            },
          });
        } else {
          setTextAreaTitle(updatedMessage, () => {
            handleChatGPTButtonsClick({
              selectedText: cleanHighlightedText,
              optionSelected,
              isPromptAction: true,
              updatedMessage,
            });
          });
          highlightedTextRef.current = "";
        }
        break;
      case SIMPLIFY_LANGUAGE:
      case IMPROVE_LANGUAGE:
      case FIX_SPELLING_GRAMMAR:
        cleanHighlightedText = cleanSelectedText({
          message: cleanHighlightedText,
        });
        if (isResponseGenerated) {
          setPromptBoxContent("");
          setTimeout(() => {
            handleChatGPTButtonsClick({
              selectedText: cleanHighlightedText,
              optionSelected,
              isPromptAction: true,
              updatedMessage,
              isSelectionExist: true,
              updatedPromptBoxContent: "",
            });
          }, 0);
          return;
        }
        if (isTextSelectionExist) {
          setPromptBoxContent("");
          setTimeout(() => {
            handleChatGPTButtonsClick({
              selectedText: promptBoxContent || cleanHighlightedText,
              optionSelected,
              isPromptAction: true,
              updatedPromptBoxContent: "",
            });
          }, 0);
        } else {
          updateTextAreaLocally({ message: updatedMessage });
          setTextAreaTitle(updatedMessage, () =>
            handleChatGPTButtonsClick({
              selectedText: cleanHighlightedText,
              optionSelected,
              isPromptAction: true,
              updatedMessage,
            })
          );
          highlightedTextRef.current = "";
        }
        break;
      case REPLACE:
        message = _.replace(
          cleanSelectedText({ message: textAreaTitle }),
          cleanSelectedText({ message: highlightedTextRef.current }),
          promptBoxContent
        );
        message = _.replace(message, bulletPointRegex, "• $1");
        updateTextAreaLocally({ message });
        setTextAreaTitle(message);
        resetPromptBox();
        resetAIMetrics();
        setTextSelected(false);
        setPromptBoxContent("");
        setIsToddleAIQuery(false);
        handleCaptionBoxFocus({ updatedMessage: message });
        highlightedTextRef.current = "";
        break;
      case ADD_BELOW: {
        const {
          preCursorText,
          postCursorText,
          placementIndex,
        } = getNextBlockIndex({
          message: handleHighlightedTextRemoval({ message: textAreaTitle }),
          cursorPositionIncludingHTML: cursorPositionIncludingHTMLRef.current,
        });
        if (isNote && _.isEqual(textAreaTitle, highlightedTextRef.current)) {
          updateTextAreaLocally({
            message: `${textAreaTitle}\n\n${promptBoxContent}`,
          });
          handleCaptionBoxFocus({
            updatedMessage: `${textAreaTitle}\n\n${promptBoxContent}`,
          });
        } else {
          updateTextAreaLocally({
            message: insertNextBlockHandler({
              preCursorText,
              postCursorText,
              placementIndex,
              contentInserted: promptBoxContent,
            }),
          });
        }
        resetPromptBox();
        resetAIMetrics();
        setTextSelected(false);
        setPromptBoxContent("");
        setIsToddleAIQuery(false);
        !(isNote && _.isEqual(textAreaTitle, highlightedTextRef.current)) &&
          handleCaptionBoxFocus({
            updatedMessage: insertNextBlockHandler({
              preCursorText,
              postCursorText,
              placementIndex,
              contentInserted: promptBoxContent,
            }),
          });
        highlightedTextRef.current = "";
        break;
      }
      case TRANSLATE:
        setPromptBoxContent("");
        setDisplayOptions("translate");
        break;
      case TRANSLATE_BACK:
        setDisplayOptions("selectedText");
        break;
      case AUTOGENERATE_CAPTION:
        setPromptBoxContent("");
        isTriggeredUnfocussed =
          !isFocussedRef && !_.isEmpty(_.trim(textAreaTitle));
        onMagicWandClick({
          callback: handleMagicWandClick,
          isTriggeredUnfocussed,
        });
        break;
    }
  };

  const onClickOutside = value => {
    let isPromptDiscardModalOpen = false;
    setIsOptionsPopoverOpen(false);
    // Removing this to handle prevent showing of discard modal on magic wand click.keeping for reference
    // if (clickedOnWandRef.current) {
    //   setIsPromptDiscardModalOpen(true);
    //   return;
    // }
    if (
      !clickedOnWandRef.current &&
      (promptBoxContentRef.current ||
        generatingResponseRef.current ||
        promptBoxRef?.current?.textContent)
    ) {
      updatePost({
        isPromptBoxTextPresent: !!promptBoxRef?.current?.textContent,
      });
      isPromptDiscardModalOpen = value;
    } else {
      resetSelection({});
    }

    setIsPromptDiscardModalOpen(isPromptDiscardModalOpen);
    clickedOnWandRef.current = false;
  };

  const handleTextSelection = event => {
    if (enableChatGPTPoweredTextFeatures) {
      if (event.keyCode === 8) {
        if (getTextAreaTitle() === highlightedTextRef.current) {
          updateTextAreaInputLocally({
            title: "",
          });
          return;
        }
      }
      if (event.keyCode === 27) {
        handleKeyDown(event);
        return;
      }
      if (
        event.target?.id === "toddle-ai" ||
        event.target?.id === "magic-wand"
      ) {
        return;
      }
      setTimeout(() => {
        const selection = window.getSelection();
        const element = textInputRef?.domElm;
        const {
          cursorPositionIncludingHTML,
          selectedText,
          cursorPosition,
        } = getCaretPositionIncludingHTML(element);

        const updatedCursorPosition = event.isTriggeredUnfocussed
          ? cursorPositionIncludingHTML
          : showPromptBox
          ? cursorPositionIncludingHTMLRef.current
          : cursorPositionIncludingHTML;

        if (
          (selection &&
            selection.toString().trim() !== "" &&
            !showPromptBoxRef.current) ||
          event.isTriggeredUnfocussed
        ) {
          const range = selection?.getRangeAt(0);
          const startIndex = getOffsetWithinElement({
            element: range.startContainer,
            offset: range.startOffset,
            textInputRef: textInputRef,
          });
          const endIndex = startIndex + selectedText.length;
          selectedTextRef.current = range.startContainer.parentElement;

          // Using the coordinates from the rect for positioning your popover
          const rect = range.getBoundingClientRect();
          if (selectedText || !highlightedTextRef.current) {
            highlightedTextRef.current = selectedText;
          }
          //Check for firefox to handle the formatted text
          if (
            _.isEqual(selectedText, textAreaTitle) &&
            _.includes(selectedText, "div") &&
            isFireFox
          ) {
            const wrappedText = `<span style="background-color: rgba\(106, 177, 255, 0.4\);padding-top: 3px;">${handleHighlightedTextRemoval(
              { message: replaceDivWithNewLine({ message: textAreaTitle }) }
            )}</span>`;
            setTextAreaTitle(wrappedText);
            highlightedTextRef.current = wrappedText;
          }
          cursorSelectionPositionRef.current = rect?.width;
          (popoverPositionRef.current = {
            top: rect.top - 44, // adding 4px for the gap
            left: rect.left,
          }),
            (clickedOnWandRef.current = false);
          cursorPositionIncludingHTMLRef.current = updatedCursorPosition;
          cursorPositionRef.current = cursorPosition;
          setIsOptionsPopoverOpen(
            event.isTriggeredUnfocussed ? false : !showPromptBoxRef.current
          );
          setIsToddleAIQuery(false);
          setDisplayOptions(
            event.isTriggeredUnfocussed
              ? _.isEmpty(promptBoxContent)
                ? "selectedText"
                : "promptBox"
              : "noSelectionWand"
          );
        } else if (!showPromptBoxRef.current) {
          highlightedTextRef.current = "";
          cursorPositionIncludingHTMLRef.current = updatedCursorPosition;
          cursorPositionRef.current = cursorPosition;
          selectedTextRef.current = null;
          setIsOptionsPopoverOpen(false);
          setTextSelected(false);
        }
      }, 0);
    }
  };

  //Note:A handler for parent component while text selection
  useImperativeHandle(
    aiInputRef,
    () => ({
      handleTextSelection,
      setDisplayOptionsDefault,
    }),
    [textInputRef]
  );

  const selectWholeText = () => {
    const captionArea = textInputRef?.domElm;
    if (window.getSelection) {
      const range = document.createRange();
      range.selectNodeContents(captionArea);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
      // Capture synchronously: the prompt-box mount focuses its
      // contenteditable and clears the window selection before
      // handleTextSelection's setTimeout fires.
      highlightedTextRef.current = captionArea?.innerHTML || "";
    }
    handleTextSelection({ isTriggeredUnfocussed: true });
  };

  const renderOptions = t => {
    return (
      <div
        style={{
          backgroundColor: colors.black,
          borderRadius: "4px",
        }}
        onClick={e => {
          e.stopPropagation();
          setTextSelected(true);
          setDisplayOptions("selectedText");
          handleHighlightedSelection({});
        }}
      >
        <div className={classes.optionsContainer}>
          <div
            className={classes.toddleAIContainer}
            onClick={() => {
              // iOS FIX: Focus hidden input first to maintain gesture chain
              triggerIOSFocus();

              setShowPromptBox(true);
              setIsOptionsPopoverOpen(false);

              // Focus actual input after state updates
              requestAnimationFrame(() => {
                focusInputElement();
              });
            }}
            data-test-id="journal-postcreation-aipopover"
          >
            <StarsTwoFilled size="xx-small" variant="on" />
            <div className={classes.toddleLabelContainer}>
              {t("common:toddle_ai")}
            </div>
          </div>
          <ConditionalWrapper
            condition={!isTablet}
            type="wrapperAsElement"
            wrapper={
              <Tooltip placement="bottom" tooltip={t("journal:elaborate")} />
            }
          >
            <IconButton
              size="x-small"
              variant="plain"
              theme="dark"
              onClick={() => {
                handlePromptOptionClick({
                  key: ELABORATE,
                  isSelectionExist: true,
                });
                setCaretPositionIncludingHTML(
                  textInputRef.domElm,
                  cursorSelectionPositionRef.current + 1
                );
              }}
              icon={<FourLinesOutlined size="x-small" variant="dark-default" />}
              style={{
                display: "flex",
              }}
              testId="journal-postcreation-aielaborate"
            />
          </ConditionalWrapper>
          <ConditionalWrapper
            condition={!isTablet}
            type="wrapperAsElement"
            wrapper={
              <Tooltip placement="bottom" tooltip={t("common:shorten")} />
            }
          >
            <IconButton
              size="x-small"
              variant="plain"
              theme="dark"
              onClick={() => {
                handlePromptOptionClick({
                  key: SHORTEN,
                  isSelectionExist: true,
                });
                setCaretPositionIncludingHTML(
                  textInputRef.domElm,
                  cursorSelectionPositionRef.current + 1
                );
              }}
              icon={<TwoLinesOutlined size="x-small" variant="dark-default" />}
              style={{
                display: "flex",
              }}
              testId="journal-postcreation-aishorten"
            />
          </ConditionalWrapper>
        </div>
      </div>
    );
  };

  const onCancel = () => {
    setIsPromptDiscardModalOpen(false);
  };

  const onDiscard = () => {
    isDiscarded.current = true;
    generatingResponse && handleResponseStop();
    resetSelection({ isDiscarded: true });
  };

  const onMagicWandClick = ({ callback: callback, isTriggeredUnfocussed }) => {
    setPromptBoxContent("");
    isDiscarded.current = false;
    setShowPromptBoxOverOptions(true);
    setTextSelected(isTriggeredUnfocussed ? true : !!selectedTextRef.current);
    setDisplayOptions(isTriggeredUnfocussed ? "promptBox" : "noSelectionWand");
    setTimeout(() => {
      callback && callback({ isTriggeredUnfocussed });
    }, 0);
  };

  const handleMagicWandClick = ({ isTriggeredUnfocussed }) => {
    isTriggeredUnfocussed && selectWholeText();
    clickedOnWandRef.current = true;
    handleChatGPTButtonsClick({
      isSelectionExist: isTriggeredUnfocussed ? true : isOptionsPopoverOpen,
      userQuery: AUTOGENERATE_WITH_IMAGE,
      updatedPromptBoxContent: "",
    });
  };

  const getMagicWandTooltip = () => {
    if (
      isToddleAIDisabled ||
      (!leId && isLeTaggingRequired) ||
      (isPortfolioParityEnabled() && !_.size(attachmentsUrlMap))
    ) {
      if (isToddleAIDisabled) {
        return t("journal:add_le_image_to_generate_caption");
      } else if (
        !_.some(attachments, attachment => attachment?.type === "IMAGE")
      ) {
        return t("journal:disable_toddle_ai_tooltip");
      } else if (!leId && isLeTaggingRequired) {
        return (
          <div style={styles.tooltipStyle}>
            {t("journal:le_not_tagged_ai_caption")}
          </div>
        );
      }
    } else {
      return t("journal:autogenerate_caption");
    }
  };

  const SelectLeContainer = ({
    position,
    childRect,
    popoverRect,
    onCloseClick,
  }) => {
    return (
      <ArrowContainer
        position={position}
        childRect={childRect}
        popoverRect={popoverRect}
        arrowColor={colors.white}
        arrowStyle={{ opacity: 1, zIndex: 10, left: "268px" }}
        arrowSize={10}
        className="popover-arrow-container"
        arrowClassName="popover-arrow"
      >
        <div className={classes.leTooltipContainer}>
          <div className={classes.leTooltipHeader}>
            <span>{t("journal:autogenerate_caption")}</span>
            <IconButton
              dsVersion="2.0"
              icon={<CloseOutlined />}
              size={"xx-small"}
              variant="neutral"
              type="plain"
              onClick={onCloseClick}
              testId="journal-postcreation-aiselectleclose"
            />
          </div>
          <div className={classes.selectLeTooltip}>
            {t("journal:select_le_tooltip")}
          </div>
          <div className={classes.leTooltipFooter}>
            <Button
              dsVersion="2.0"
              variant={"neutral"}
              type="outlined"
              size={"small"}
              className={classes.selectLeButton}
              onClick={() => {
                setShowLEtooltip(false);
                if (_.size(elementPYP)) {
                  setShowGoalsRemovalWarningModal(true);
                  return;
                }
                goToStep("TAG_LEARNING_GOALS_FROM_ASSESSMENTS");
                updatePost({ isMagicWandClick: true });
              }}
              testId="journal-postcreation-aiselectle"
            >
              {t("journal:select_learning_experience")}
            </Button>
          </div>
        </div>
      </ArrowContainer>
    );
  };

  const resetLearningGoalsData = () => {
    updatePost({
      // clear main properties of learning goals
      unitPlan: null,
      isResourceSkipped: false,
      resourceId: null,
      resourceType: null,
      resourceItem: undefined,
      elementPYP: undefined,
      // Clear properties used for backup
      originalUnitPlan: null,
      originalIsResourceSkipped: false,
      originalResourceId: null,
      originalResourceType: null,
      originalResourceItem: undefined,
      originalElementPYP: undefined,
      selectFromAllPYP: undefined,
      // clear ratings as well
      ibPYPElementRatings: [],
      originalibPYPElementRatings: [],
      showTagLearningGoalsModal: false,
    });
  };

  const onChangeLearningsGoals = () => {
    resetLearningGoalsData();
    setShowGoalsRemovalWarningModal(false);
    goToStep("TAG_LEARNING_GOALS_FROM_ASSESSMENTS");
  };

  const createMagicWandAIConversation = async () => {
    if (!_.get(props, "aiConversationId")) {
      await createAIConversation();
    }
    onSendQuery({
      content: "PORTFOLIO_MAGIC_WAND_CLICK",
      metaData: {
        isUserInputMessage: false,
      },
      authorType: "assistant",
    });
  };

  const menuOptions = useMemo(
    () =>
      generatingResponse
        ? []
        : getMenuOptions({
            t,
            displayOptions,
            languageOptions: LANGUAGE_ACTIONS,
            defaultLanguagePreference: props.defaultLanguagePreference,
            showAutoGenerateCaption:
              showMagicWand &&
              _.size(attachmentsUrlMap) > 0 &&
              !_.isEmpty(leId) &&
              !isImageUploading,
          }),
    [
      generatingResponse,
      displayOptions,
      showMagicWand,
      leId,
      attachmentsUrlMap,
      props.defaultLanguagePreference,
      t,
      isImageUploading,
    ]
  );

  return (
    <div>
      {/* iOS Hidden Input - React way */}
      {isIOSDevice() && (
        <input
          ref={iosFocusInputRef}
          style={styles.hiddenInput}
          aria-hidden="true"
          tabIndex={-1}
          readOnly
        />
      )}

      {showPromptBox ? (
        <div
          style={{
            width: "102%",
            transform: `translate(
                -8px,
                ${textAreaTitle ? textInputRef?.domElm?.clientHeight : -2}px)
              `,
            ...customPromptBoxStyles,
          }}
        >
          <AIPromptBoxV2
            ref={promptBoxRef}
            menuOptions={menuOptions}
            response={promptBoxContent}
            onClickOutside={onClickOutside}
            showModerationAlert={userInputFlagged}
            moderationAlertText={t("common:user_input_flagged_msg")}
            onChange={value => {
              setPromptBoxKeywords(value);
              !isPromptBoxTextPresent &&
                updatePost({ isPromptBoxTextPresent: true });
            }}
            onSubmit={async e => {
              if (!isImageUploading) {
                setDisplayOptions("noSelectionWand");
                setIsToddleAIQuery(false);
                setShowPromptBoxOverOptions(true);
                handlePromptOptionClick({ key: CHANGE_THIS });
                if (!_.get(props, "aiConversationId")) {
                  await createAIConversation();
                }
                await onSendQuery({
                  content: promptBoxKeywords,
                  metaData: {
                    isUserInputMessage: true,
                  },
                  authorType: "user",
                });
              }
            }}
            streamingActionText={streamingText}
            stopStreamingButtonText={
              isTabletByUserAgent() ? t("common:stop") : t("common:stop_esc")
            }
            onStop={() => {
              handleResponseStop(
                isResponseGenerated || generatingResponseRef.current
                  ? _.isEmpty(promptBoxContent)
                    ? _.isEqual(
                        handleHighlightedTextRemoval({
                          message: prevTextRef.current,
                        }),
                        handleHighlightedTextRemoval({
                          message: textAreaTitle,
                        })
                      )
                    : false
                  : _.isEqual(
                      handleHighlightedTextRemoval({
                        message: prevTextRef.current,
                      }),
                      handleHighlightedTextRemoval({ message: textAreaTitle })
                    )
              );
            }}
            isStreaming={generatingResponse}
            placeholder={
              _.isEmpty(textAreaTitle)
                ? placeholder
                : t("journal:change_ai_caption")
            }
            onOptionClick={handlePromptOptionClick}
            showFeedbackStrip={!!usageMetricsId}
            feedbackStrip={
              <AIFeedbackStrip
                usageMetricsId={usageMetricsId}
                model={model}
                temperature={currentTemperature}
                containerStyle={{
                  ...(promptBoxContent
                    ? styles.aiFeedbackStrip
                    : styles.aiFeedbackStripNoContent),
                  backgroundColor: colors.white,
                  gap: "8px",
                  justifyContent: "left",
                }}
                showFeedbackStripV2={true}
                iconContainerStyle={styles.aiFeedbackStripIconContainer}
              />
            }
            responseStyle={{
              ...responseFontStyles,
              ...{
                textAlign: isRTLText(promptBoxContent) ? "right" : "left",
              },
            }}
            testId="journal-postcreation-aipromptbox"
            menuStyle={getMenuOptionsStyle(_.size(menuOptions))}
            aiVoiceComponent={
              <div style={styles.aiVoiceContainerStyle}>
                {" "}
                {!_.isEmpty(configureAIVoiceProps) &&
                isPromptSetSelectionEnabled ? (
                  <ConfigureAIVoiceV2 {...configureAIVoiceProps} />
                ) : null}
                <AudioRecorderSelector
                  showValue={false}
                  version="inline"
                  showProgress={false}
                  parentId={aiConversationId}
                  parentType="AI_CONVERSATION"
                  header={t("common:add_voice_message")}
                  disabled={isAudioLoading}
                  setChatbarVisible={handleSetChatbarVisible}
                  onFileUpload={handleFileUpload}
                  onBlobAttachmentRecieved={handleSttTranscription}
                  recordButtonRef={micButtonRef}
                  buttonComponent={
                    <IconButton
                      dsVersion="2.0"
                      variant="neutral"
                      type="plain"
                      size="small"
                      icon={<MicrophoneOutlined />}
                      disabled={isAudioLoading}
                      aria-label={t("common:record_voice")}
                      testId="journal-postcreation-stt-microphone"
                      ref={micButtonRef}
                    />
                  }
                  alertContainerStyle={styles.alertContainerStyle}
                />
              </div>
            }
          />
        </div>
      ) : null}
      {isOptionsPopoverOpen && textAreaTitle ? (
        <Popover
          isOpen={isOptionsPopoverOpen}
          align={"start"}
          positions={["top", "top"]}
          padding={4}
          reposition={true}
          containerStyle={{
            ...styles.popoverContainer,
            transform: `translate(
                ${popoverPositionRef.current.left}px,
                ${popoverPositionRef.current.top}px)
              `,
          }}
          content={renderOptions(t)}
          ref={optionsPopoverRef}
          childrenRef={selectedTextRef}
          isChildrenRefPassed={true}
        />
      ) : null}
      {!showPromptBox ? (
        <div
          style={{
            marginRight: "12px",
            marginTop: "16px",
            display: "flex",
            ...promptIconStyles,
          }}
        >
          {showMagicWand ? (
            <ConditionalWrapper
              type={"wrapperAsElement"}
              condition={!showLEtooltip && !isTablet}
              wrapper={<Tooltip tooltip={getMagicWandTooltip()} />}
            >
              <IconButton
                dsVersion="2.0"
                size={aiIconSize}
                variant="neutral"
                type="plain"
                id="magic-wand"
                icon={<MagicWandThreeStarsOutlined size={magicWandIconSize} />}
                style={{
                  display: "flex",
                }}
                ref={popoverContainerRef}
                onClick={() => {
                  if (
                    !leId &&
                    isLeTaggingRequired &&
                    isPortfolioParityEnabled()
                  ) {
                    setShowLEtooltip(true);
                    return;
                  }
                  createMagicWandAIConversation();
                  const isTriggeredUnfocussed = !_.isEmpty(
                    _.trim(textAreaTitle)
                  );
                  setStreamingText(
                    _.size(attachmentsUrlMap)
                      ? _.size(attachmentsUrlMap) > 1
                        ? t("journal:anonymising_image_plural")
                        : t("journal:anonymising_image")
                      : t("common:writing")
                  );
                  onMagicWandClick({
                    callback: handleMagicWandClick,
                    isTriggeredUnfocussed,
                  });
                }}
                disabled={
                  isImageUploading
                    ? true
                    : isPortfolioParityEnabled()
                    ? !_.size(attachmentsUrlMap)
                    : isToddleAIDisabled || (!leId && isLeTaggingRequired)
                }
                testId="journal-postcreation-aimagicwand"
              />
            </ConditionalWrapper>
          ) : null}
          <ConditionalWrapper
            condition={!isTablet}
            type="wrapperAsElement"
            wrapper={
              <Tooltip
                tooltip={
                  textAreaTitle
                    ? t("journal:edit_with_ai")
                    : t("journal:write_with_ai")
                }
              />
            }
          >
            <IconButton
              dsVersion="2.0"
              size={aiIconSize}
              variant={aiIconVariant}
              type="plain"
              id="toddle-ai"
              shouldStopPropagation
              icon={
                useCustomAIIcon ? (
                  <AIOutlined
                    fill={
                      isImageUploading ? colors.iconOnSubtle : colors.neutral400
                    }
                    height={32}
                  />
                ) : (
                  <AiOutlined size="small" />
                )
              }
              style={{
                display: "flex",
                ...aiButtonStyle,
              }}
              disabled={isImageUploading}
              onClick={() => {
                if (isIpad() || isIphone()) {
                  handlePromptBoxFocus();
                }
                // iOS FIX: Focus hidden input first to maintain gesture chain
                triggerIOSFocus();

                !_.get(props, "aiConversationId") && createAIConversation();
                isDiscarded.current = false;
                const isTriggeredUnfocussed =
                  !_.isEmpty(_.trim(textAreaTitle)) &&
                  !highlightedTextRef.current;
                isTriggeredUnfocussed && selectWholeText();
                setShowPromptBox(!showPromptBox);
                setIsOptionsPopoverOpen(false);
                setIsToddleAIQuery(!isToddleAIQuery);
                setTextSelected(
                  isTriggeredUnfocussed ? true : !!selectedTextRef.current
                );
                setPromptBoxContent("");
                setShowPromptBoxOverOptions(!_.isEmpty(getTextAreaTitle()));
                setDisplayOptions(
                  highlightedTextRef.current
                    ? "selectedText"
                    : "selectedTextDisabled"
                );
                setIsResponseGenerated(false);

                // Focus actual input after state updates
                requestAnimationFrame(() => {
                  focusInputElement();
                  handleHighlightedSelection({});
                });
              }}
              testId="journal-postcreation-aiicon"
            />
          </ConditionalWrapper>
        </div>
      ) : null}

      {isPromptDiscardModalOpen ? (
        <div onClick={e => e.stopPropagation()}>
          <Modal
            width="440px"
            height={"fit-content"}
            isOpen={isPromptDiscardModalOpen}
            onClose={onCancel}
          >
            <Modal.Header isClosable>
              <div className={classes.headerContainer}>
                {generatingResponse || isResponseGenerated ? (
                  <div className={classes.aiIcon}>
                    <StarsTwoFilled variant={"link"} size={"xx-small"} />
                  </div>
                ) : null}
                <div className={classes.titleContainer}>
                  {promptBoxKeywords && !generatingResponse
                    ? t("journal:discard_prompt")
                    : t("common:discard_toddle_ai_title")}
                </div>
              </div>
            </Modal.Header>
            <Modal.Body>
              {promptBoxKeywords && !generatingResponse
                ? t("journal:discard_prompt_message")
                : t("journal:discard_toddle_ai_body")}
            </Modal.Body>
            <Modal.Footer>
              <div className={classes.footerContainer}>
                <Button
                  size="medium"
                  variant="outlined-subtle"
                  onClick={onCancel}
                  testId="journal-postcreation-aikeep"
                >
                  {promptBoxKeywords && !generatingResponse
                    ? t("journal:continue_writing")
                    : t("common:keep")}
                </Button>
                <Button
                  size="medium"
                  variant="destructive"
                  onClick={onDiscard}
                  testId="journal-postcreation-aidiscard"
                >
                  {promptBoxKeywords && !generatingResponse
                    ? t("common:discard_and_exit")
                    : t("common:discard")}
                </Button>
              </div>
            </Modal.Footer>
          </Modal>
        </div>
      ) : null}
      {showLEtooltip ? (
        <Popover
          isOpen={showLEtooltip}
          containerClassName={classes.popoverContainer}
          containerStyle={{ zIndex: 2500, marginLeft: "20px" }}
          positions={["top"]}
          align="end"
          reposition={true}
          content={({ position, childRect, popoverRect }) => (
            <SelectLeContainer
              position={position}
              popoverRect={popoverRect}
              childRect={childRect}
              onCloseClick={() => setShowLEtooltip(false)}
            />
          )}
          childrenRef={popoverContainerRef}
          isChildrenRefPassed={true}
        ></Popover>
      ) : null}
      {showGoalsRemovalWarningModal ? (
        <Dialog
          title={`${t("journal:select_le_assessment_heading")}?`}
          onClose={() => {
            setShowGoalsRemovalWarningModal(false);
          }}
          buttonOneText={t("common:keep")}
          buttonTwoText={t("journal:discard_and_select_new")}
          onButtonOneClick={() => {
            setShowGoalsRemovalWarningModal(false);
          }}
          onButtonTwoClick={onChangeLearningsGoals}
          message={t("journal:select_le_warning_message")}
          testId="journal-postcreation-overideselectedledialog"
        />
      ) : null}
    </div>
  );
}

const mapStateToProps = (state, ownProps) => {
  const postDetails = state.post.postDetails;
  const userType = state.login.userInfo.user_type;
  const {
    title,
    attachments,
    unitPlan,
    resourceItem,
    students,
    postTaggedEntity,
  } = postDetails;
  const isNewGoalTaggingEnabled = getIsNewGoalTaggingEnabled();
  const courseId = _.get(
    state,
    "teacher.selected_class.selected_course",
    BLANK_STRING
  );
  const attachmentsUrlMap = _.map(
    _.filter(attachments, attachment => attachment?.type === "IMAGE"),
    attachment => _.get(attachment, "url", "")
  );
  const unitPlanObject =
    isNewGoalTaggingEnabled && isUnitPlanOrAssessmentTagged(postTaggedEntity)
      ? isAssessmentTagged(postTaggedEntity)
        ? _.first(postTaggedEntity)?.entity?.unitPlan
        : _.first(postTaggedEntity)?.entity
      : {};

  const unitPlanId = _.get(
    isNewGoalTaggingEnabled ? unitPlanObject : unitPlan,
    "id",
    BLANK_STRING
  );
  const unitPlanTitle = _.get(
    isNewGoalTaggingEnabled ? unitPlanObject : unitPlan,
    "title.value",
    BLANK_STRING
  );
  let taggedFolders = _.get(postDetails, "folders", BLANK_ARRAY);
  const addedFolders = _.get(postDetails, "addedFolders", BLANK_ARRAY);
  taggedFolders = [...taggedFolders, ...addedFolders];
  const leId = _.get(
    isNewGoalTaggingEnabled && isAssessmentTagged(postTaggedEntity)
      ? _.first(postTaggedEntity)?.entity
      : resourceItem,
    "id",
    BLANK_STRING
  );
  const elementPYP = _.get(postDetails, "elementPYP", BLANK_ARRAY);
  const selectFromAllPYP = _.get(postDetails, "selectFromAllPYP", false);

  const studentIds = _.map(
    _.get(students, "edges", BLANK_ARRAY),
    student => student?.node?.id
  );

  const studentMaskedIds = _.map(
    _.compact(studentIds),
    student => `⌴st-${student}⌴`
  );

  const studentIdToNameMap = {};
  _.map(_.get(students, "edges", BLANK_ARRAY), (student, index) => {
    const sameFirstNameStudents = _.filter(
      _.slice(_.get(students, "edges", BLANK_ARRAY), 0, index),
      otherStudent => {
        return otherStudent?.node?.firstName === student?.node?.firstName;
      }
    );
    if (sameFirstNameStudents?.length > 0) {
      // If there are other students with the same first name, add full name for all
      _.forEach([student, ...sameFirstNameStudents], studentItr => {
        studentIdToNameMap[
          studentItr?.node?.id
        ] = getFormattedUserNameWithoutPronoun({ user: studentItr?.node });
      });
    } else {
      // If no other students with the same first name, add only the first name
      studentIdToNameMap[student?.node?.id] = `${
        student?.node?.preferredName
          ? student?.node?.preferredName
          : student?.node?.firstName
      }`;
    }
  });
  const curriculumProgramId = state.platform.currentCurriculumProgram.id;
  const userName = `${_.get(state, "login.userInfo.first_name", "")} ${_.get(
    state,
    "login.userInfo.last_name",
    ""
  )}`;
  const isPromptBoxTextPresent = _.get(
    state,
    "post.postDetails.isPromptBoxTextPresent",
    false
  );
  const aiConversationId = _.get(state, "post.postDetails.aiConversationId");

  const maxAttachmentCount = getSettingValue({
    name: "PortfolioAttachmentCount",
    organizationId: state.login.userInfo.org_id,
  });

  const isUploadingInProgress = !_.isEmpty(
    _.get(state, "app_services.progressOfUploads", {})
  );

  const userId = _.get(state, "login.userInfo.id");
  const organizationId = _.get(state, "login.userInfo.org_id", "");

  const defaultLanguagePreferenceId = getSettingValue({
    name: "ConfigurePortfolioAIVoiceLanguage",
    userId,
    organizationId,
    readFromUserAndOrgLevel: true,
  });

  const defaultLanguagePreference =
    _.find(AI_TRANSLATION_LANGUAGES, {
      id: defaultLanguagePreferenceId,
    }) || _.find(AI_TRANSLATION_LANGUAGES, { code: "en" });

  return {
    title,
    attachmentsUrlMap,
    courseId,
    unitPlanId,
    leId,
    studentIds: _.compact(studentIds),
    studentIdToNameMap,
    studentMaskedIds,
    attachments,
    elementPYP,
    selectFromAllPYP,
    curriculumProgramId,
    userType,
    unitPlanTitle,
    taggedFolders,
    userName,
    isPromptBoxTextPresent,
    aiConversationId,
    maxAttachmentCount,
    defaultLanguagePreference,
    isUploadingInProgress,
    isPromptSetSelectionEnabled: getSettingValue({
      name: "Common:EnableAIPromptSetSelection",
      readFromUserAndOrgLevel: true,
    }),
  };
};

const mapActionCreators = {
  generateAIResponse,
  uploadAttachments,
  updatePost,
  createAIConversation,
  onSendQuery,
  setToastMsg,
  updateSettings,
  getSpeechToTextTranscript,
};

export default compose(connect(mapStateToProps, mapActionCreators))(
  React.memo(forwardRef(AIInputBox))
);
